import {PeerTypeFactories, State} from '../../state/types/state.js';
import {PeerType} from '../../state/types/factory.js';
import {PeerId} from '../../state/types/common.js';
import {castDraft, Draft, produce} from 'immer';
import {removev} from '../../common/utilities.js';
import {throwReason} from '../../reason.js';
import {activityInvalid} from './constants.js';
import {Activity, ActivityId, ActivityType} from '../../state/types/activity.js';


export function addFactories(state: State, factoryTypes: PeerType[], peerId: PeerId): State {
    return factoryTypes.reduce((s: State, ft: PeerType) => {
        return produce(s, (d: Draft<State>) => {
            if (!d.factories) {
                d.factories = {};
            }
            if (!d.factories[ft]) {
                d.factories[ft] = [];
            }
            d.factories[ft].push(peerId);
        });
    }, state);
}


export function removeFactories(state: State, factoryTypes: PeerType[], peerId: PeerId): State {
    if (factoryTypes.length > 0 && peerId) {
        return factoryTypes.reduce((s: State, ft: PeerType) => {
            return produce(s, (draft: Draft<State>) => {
                if (draft.factories) {
                    draft.factories[ft] = removev(peerId, draft.factories[ft]);
                    if (draft.factories[ft].length == 0) {
                        delete draft.factories[ft];
                    }
                    if (Object.keys(draft.factories).length === 0) {
                        delete draft.factories;
                    }
                }
            });
        }, state);

    }
    return state;
}

export function factories(state: State): PeerTypeFactories | undefined {
    return state.factories;
}

export function factoriesByType(state: State, peerType: PeerType): ReadonlyArray<PeerId> | undefined {
    return state.factories?.[peerType];
}

// activities
export function activity(state: State, activityId?: ActivityId): Activity | undefined {
    if (activityId) {
        return state.activities?.[activityId];
    }
}

export function setActivity(state: State, activityId: string | undefined, activity): State {
    if (activityId) {
        return produce(state, (draft: Draft<State>) => {
            draft.activities = draft.activities || {};
            draft.activities[activityId] = activity;
        });
    }
    return state;
}

export function removeActivity(state: State, activityId: string): State {
    return produce(state, (draft: Draft<State>) => {
        if (draft.activities) {
            delete draft.activities[activityId];
            if (Object.keys(draft.activities).length === 0) {
                delete draft.activities;
            }
        }
    });
}

export function activities(state: State): Activity[] {
    return Object.values<Activity>(state.activities ?? {});
}

// activity types

export function activityTypes(state: State, user?: string): { [type: string]: ActivityType } | undefined {
    if (user) {
        return state.activityTypes?.[user];
    }
}

export function addActivityTypes(state: State, user: string, types: ActivityType[]): State {
    return types.reduce((s: State, type) => {
        return produce(s, (draft: Draft<State>) => {
            draft.activityTypes = draft.activityTypes || {};
            draft.activityTypes[user] = draft.activityTypes[user] || {};
            draft.activityTypes[user][type.name] = castDraft(type);
        });
    }, state);
}

export function removeActivityTypes(state: State, user: string, typesNames: string[]): State {
    return Array.from(typesNames).reduce((s: State, typeName: string) => {
        return produce(s, (draft: Draft<State>) => {
            if (draft.activityTypes) {
                if (draft.activityTypes[user]) {
                    delete draft.activityTypes[user][typeName];
                    if (Object.keys(draft.activityTypes[user]).length === 0) {
                        delete draft.activityTypes[user];
                    }
                }
                if (Object.keys(draft.activityTypes).length === 0) {
                    delete draft.activityTypes;
                }
            }
        });
    }, state);
}


/**
 * Returns a list of ids for the peers that have subscribed for a given activity type
 */
export function activitySubscribers(state: State, activityType: string) {
    const result = new Set<PeerId>();
    state.activitySubscribers?.byType?.[activityType]?.forEach(id => result.add(id));
    state.activitySubscribers?.all?.forEach(id => result.add(id));
    return result;
}

export function addActivitySubscriber(state: State, peerId: PeerId, activityType: true | string): State {
    return produce(state, (draft: Draft<State>) => {
        draft.activitySubscribers = draft.activitySubscribers || {};
        if (activityType === true) { // all
            draft.activitySubscribers.all = draft.activitySubscribers.all || new Set<PeerId>();
            draft.activitySubscribers.all.add(peerId);
        } else {
            draft.activitySubscribers.byType = draft.activitySubscribers.byType || {};
            draft.activitySubscribers.byType[activityType] = draft.activitySubscribers.byType[activityType] || new Set<PeerId>();
            draft.activitySubscribers.byType[activityType].add(peerId);
        }
    });
}

export function removeActivitySubscriber(state: State, peerId: PeerId, activityType: true | string): State {
    return produce(state, (draft: Draft<State>) => {
        if (draft.activitySubscribers) {
            if (activityType === true) { // all
                if (draft.activitySubscribers.all) {
                    draft.activitySubscribers.all.delete(peerId);
                    if (draft.activitySubscribers.all.size === 0) {
                        delete draft.activitySubscribers.all;
                    }
                }
            } else {
                if (draft.activitySubscribers.byType) {
                    if (draft.activitySubscribers.byType[activityType]) {
                        draft.activitySubscribers.byType[activityType].delete(peerId);
                        if (draft.activitySubscribers.byType[activityType].size === 0) {
                            delete draft.activitySubscribers.byType[activityType];
                        }
                    }
                    if (Object.keys(draft.activitySubscribers.byType).length === 0) {
                        delete draft.activitySubscribers.byType;
                    }
                }
            }
            if (Object.keys(draft.activitySubscribers).length === 0) {
                delete draft.activitySubscribers;
            }
        }
    });
}


export function activityThrows(state: State, activityId: ActivityId): Activity  | never {
    const a = activity(state, activityId);
    if (a) {
        return a;
    }
    throwReason(activityInvalid, `Unable to find activity with id ${activityId}`);
}

/**
 * Retrieves an activity with a given id, throwing an exception if missing
 */
export function activityMaybeThrows(state: State, activityId?: ActivityId): Activity | undefined | never {
    if (activityId) {
        return activityThrows(state, activityId);
    }
}

